"use client";

import React, { useState } from "react";
import axios from "axios";
import { Calendar, Phone, User, Mail, AtSign } from "lucide-react";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import useAuthStore from "@/lib/store/authStore";
import { motion } from "framer-motion";


// Componentes reutilizables
import ImageUploader from "@/app/components/SignUp/ImageUploader";
import FormField from "@/app/components/SignUp/FormField";
import SelectField from "@/app/components/SignUp/SelectField";
import ActionButtons from "@/app/components/SignUp/ActionButtons";
import EmailDisplay from "@/app/components/SignUp/EmailDisplay";

interface UserType {
  rolId: number;
  codigo: number;
  authority: string;
  descripcion: string;
  permisos: string;
}

interface Props {
  email: string;
  password: string;
  step: number;
  setStep: (step: number) => void;
  setOpen: (open: boolean) => void;
  roleData: UserType;
}

interface ImagenPerfil {
  tipo: string;
  nombre: string;
  archivo: string;
}

const UsuarioSignUp = ({
  email,
  password,
  setOpen,
  step,
  setStep,
  roleData,
}: Props) => {
  // Estados para los datos del backend
  const [imagenPerfil, setImagenPerfil] = useState<ImagenPerfil>({
    tipo: "",
    nombre: "",
    archivo: "",
  });
  const [username, setUsername] = useState("");
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [fechaNacimiento, setFechaNacimiento] = useState("");
  const [sexo, setSexo] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Estados para la validación
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isNombreValid, setIsNombreValid] = useState(true);
  const [isTelefonoValid, setIsTelefonoValid] = useState(true);
  const [isSexoValid, setIsSexoValid] = useState(true);
  const [isFechaNacimientoValid, setIsFechaNacimientoValid] = useState(true);
  const [isImagenValid, setIsImagenValid] = useState(true);
  const [isUsernameValid, setIsUsernameValid] = useState(true);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
  };

  const handleNombreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNombre(value);
    if (formSubmitted) {
      setIsNombreValid(value.trim().length > 0);
    }
  };

  const handleTelefonoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTelefono(value);
    if (formSubmitted) {
      setIsTelefonoValid(/^\d{10,15}$/.test(value));
    }
  };

  const handleFechaNacimientoChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setFechaNacimiento(value);
    if (formSubmitted) {
      setIsFechaNacimientoValid(value.trim().length > 0);
    }
  };

  const handleSexoChange = (value: string) => {
    setSexo(value);
    if (formSubmitted) {
      setIsSexoValid(value.trim().length > 0);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagenPerfil({
          tipo: file.type,
          nombre: file.name,
          archivo: reader.result as string,
        });
        if (formSubmitted) {
          setIsImagenValid(true);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    setFormSubmitted(true);
    setIsNombreValid(nombre.trim().length > 0);
    setIsTelefonoValid(/^\d{10,15}$/.test(telefono));
    setIsSexoValid(sexo.trim().length > 0);
    setIsFechaNacimientoValid(fechaNacimiento.trim().length > 0);
    setIsImagenValid(true);
    setIsUsernameValid(username.trim().length > 0);
  };

  const handleSubmit = async () => {
    validateForm();

    if (!isFormValid) {
      toast.error("Por favor complete todos los campos requeridos");
      return;
    }

    setIsLoading(true);

    const API_URL = process.env.NEXT_PUBLIC_API_URL as string;

    // Formatear fecha de nacimiento a MM-DD-YYYY
    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    };

    const defaultImageBase64 = '/surferDefault.png';

    const dataToSend = {
      usuarioId: 0,
      username: email,
      userName: email,
      nombre,
      password,
      fotoPerfil: imagenPerfil.archivo || defaultImageBase64,
      telefono,
      fechaNacimiento: fechaNacimiento
        ? formatDate(fechaNacimiento)
        : formatDate(new Date().toISOString()),
      authority: roleData.authority || "ROLE_USER",
      nombreRol: roleData.descripcion || "USER",
      imagenPerfil: {
        tipo: imagenPerfil.tipo || "image/png",
        nombre: imagenPerfil.nombre || "default_profile",
        archivo: imagenPerfil.archivo || defaultImageBase64,
      },
      sexo: sexo || "",
    };

    try {
      const response = await axios.post(`${API_URL}/registerUser`, dataToSend);

      if (response.status === 200 && response.data && response.data.jwttoken) {
        // Guardar token y usuarioId en cookies
        Cookies.set("t_u", response.data.jwttoken, {
          path: "/", // La cookie estará disponible en toda la aplicación
          secure: true, // Solo permite que la cookie se envíe a través de HTTPS
          sameSite: "Strict", // Evita el envío de cookies a otros dominios (CSRF)
          expires: 1, // Opcional: la cookie expira después de 1 día
        });

        Cookies.set("u_i_d", response.data.usuarioId, {
          path: "/",
          secure: true,
          sameSite: "Strict",
          expires: 1,
        });

        Cookies.set("r_u", response.data.rol, {
          path: "/",
          secure: true,
          sameSite: "Strict",
          expires: 1,
        });

        useAuthStore.getState().setAuthData({
          rol: response.data.rol,
          jwttoken: response.data.jwttoken,
          token: response.data.token,
        });
        toast.success("¡Registro exitoso!");
        setOpen(true);
      }
    } catch (error: any) {
      if (error.response) {
        const errorData = error.response.data;

        const mensajeError =
          Array.isArray(errorData?.subErrors) && errorData.subErrors.length > 0
            ? errorData.subErrors[0].mensaje
            : "Hubo un error en el registro";

        toast.error(mensajeError);
      } else if (error.request) {
        toast.error("Error de conexión. Por favor, intente nuevamente");
      } else {
        toast.error("Error al procesar la solicitud");
      }
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid =
    isNombreValid &&
    isTelefonoValid &&
    isSexoValid &&
    isFechaNacimientoValid &&
    isUsernameValid;

  const sexoOptions = [
    { value: "M", label: "Masculino" },
    { value: "F", label: "Femenino" },
    { value: "O", label: "Otro" },
  ];

  return (
    <div className="w-full relative pb-20 px-4 sm:px-6 max-w-7xl mx-auto">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 sm:p-10 rounded-xl mb-12 shadow-md">
        <div className="flex flex-col items-center justify-center">
          <h2 className="text-xl md:text-2xl sm:text-3xl font-bold text-gray-800 mb-8">Perfil de Usuario</h2>

          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 mb-4">
            <ImageUploader
              imagenPerfil={imagenPerfil}
              handleImageUpload={handleImageUpload}
              isImagenValid={true}
              formSubmitted={formSubmitted}
              type="usuario"
              required={false}
            />

            <div className="flex flex-col items-center md:items-start">
              <p className="text-sm text-gray-500 mb-2">Correo electrónico:</p>
              <EmailDisplay
                email={email}
                style="instructor"
                className="mb-4"
              />
            </div>
          </div>

        </div>
      </div>

      <motion.div
        className=" grid grid-cols-1 gap-x-12 gap-y-10 px-2 sm:px-4 max-w-3xl mx-auto"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Información personal */}
        <div className="space-y-8 " >
          <div className="border-b border-gray-200 pb-3 mb-6">
            <h3 className="text-base font-medium text-gray-700">Información Personal</h3>
          </div>

          {/* Nombre completo */}
          <FormField
            id="nombre"
            label="Nombre y Apellido"
            value={nombre}
            onChange={handleNombreChange}
            placeholder="Ingresa tu nombre completo"
            required
            isValid={isNombreValid}
            formSubmitted={formSubmitted}
            errorMessage="El nombre es requerido"
            className="mb-6"
            icon={<User />}
          />


          {/* Teléfono */}
          <FormField
            id="telefono"
            label="Teléfono"
            value={telefono}
            onChange={handleTelefonoChange}
            placeholder="Ingresa tu número de teléfono"
            required
            isValid={isTelefonoValid}
            formSubmitted={formSubmitted}
            errorMessage="Número de teléfono inválido"
            className="mb-6"
            icon={<Phone />}
          />

          {/* Sexo */}
          <SelectField
            id="sexo"
            label="Sexo"
            value={sexo}
            onValueChange={handleSexoChange}
            options={sexoOptions}
            placeholder="Selecciona tu sexo"
            required
            isValid={isSexoValid}
            formSubmitted={formSubmitted}
            errorMessage="Selecciona tu sexo"
            className="mb-6"
            icon={<User />}
          />

          {/* Fecha de nacimiento */}
          <FormField
            id="fechaNacimiento"
            label="Fecha de nacimiento"
            value={fechaNacimiento}
            onChange={handleFechaNacimientoChange}
            type="date"
            required
            isValid={isFechaNacimientoValid}
            formSubmitted={formSubmitted}
            errorMessage="La fecha de nacimiento es requerida"
            className="mb-6"
            icon={<Calendar />}
          />
        </div>

        {/* Botones de acción fijos */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-10">
          <div className="max-w-5xl mx-auto">
            <ActionButtons
              onBack={() => setStep(1)}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              isFormValid={isFormValid}
              buttonStyle="instructor"
              submitLabel="Completar Registro"
              backLabel="Paso Anterior"
            />
          </div>
        </div>
      </motion.div>


    </div>
  );
};

export default UsuarioSignUp;
